import styled, { createGlobalStyle } from 'styled-components';

import { ANIM, COLORS, FIXED_HEADER, LAYOUT_CHROME, LUMIO_DESKTOP, NAV_DESKTOP } from '../../theme/tokens';

/*
 * The legacy chrome classNames (`pg-layout`, `pg-body`, `pg-body-top`,
 * `pg-body-content`) are kept on these elements ON PURPOSE: PageShell's global
 * header-tuck/scrim rules (`ContentTuck`, `HeaderScrim` anchoring) target them,
 * and the pages' 112px desktop top padding only makes sense WITH the -88px tuck
 * those rules apply. Drop a className here and every page gains a dead gap.
 */

/* Template `.pg-layout`. */
export const Root = styled.div.attrs({ className: 'pg-layout' })`
  width: 100dvw;
  /* Reserve space for the fixed support-impersonation strip with PADDING, not margin:
     a top margin collapses through #root and scrolls the whole page by the strip's
     height. box-sizing is border-box (Tailwind preflight), so height stays 100dvh and
     the content area is the remainder. --ows-imp-offset is the strip's measured height
     (0 when absent), set by MainLayout; on mobile FIXED_HEADER frees the height and the
     padding still clears the strip, with the document scrolling. */
  height: 100dvh;
  padding-top: var(--ows-imp-offset, 0px);
  display: flex;
  gap: ${LAYOUT_CHROME.gap};
  position: relative;
  max-width: ${LAYOUT_CHROME.maxWidth};
  margin: 0 auto;
  overflow: hidden;
  /* The studio's own page backdrop. The columns (side menu / Lumio) and the gaps
   * around the content card sit on this; in dark it's the same near-black as
   * before (no change), in light it's the soft off-white. Without it the
   * config-engine's dark body background would show through the whole studio. */
  background: ${COLORS.pageBg};

  ${NAV_DESKTOP} {
    /* No bottom pad on desktop: the content card reaches the viewport edge
     * instead of stopping short above a dead gap. The impersonation strip offset
     * rides in the top padding alongside the chrome inset. */
    padding: calc(${LAYOUT_CHROME.pad} + var(--ows-imp-offset, 0px)) ${LAYOUT_CHROME.pad} 0;
  }

  ${FIXED_HEADER} {
    /* Mobile: stop being a fixed-height, clipped flex box. The DOCUMENT scrolls
     * here (so the browser chrome reacts — see MobileBodyScroll), so this grows
     * with content (min 100dvh fills a short page) and never clips it. Dropping
     * flex avoids a stretched flex child re-pinning the body to the viewport. */
    display: block;
    height: auto;
    min-height: 100dvh;
    overflow: visible;
  }
`;

/**
 * Studio document backdrop. While the studio is mounted, paint the whole
 * document (html / body / #root) with the themed page background so the
 * config-engine's own dark body background can't show through — behind the
 * `.pg-layout` on ultrawide screens (where it's narrower than the viewport) or
 * any gap. Unmounts with the layout, restoring the config shell's background for
 * the config-driven pages. Dark = the same near-black as before; light = off-white.
 */
export const StudioBackdrop = createGlobalStyle`
  html,
  body,
  #root {
    background: ${COLORS.pageBg} !important;
  }
`;

/**
 * Immersive (full-screen Advanced Video editor) layout collapse. Mounted by MainLayout ONLY while
 * immersive, and every rule is desktop-only (${'NAV_DESKTOP'}) AND scoped to `.pg-layout[data-immersive]`,
 * so no other page and no mobile viewport is ever affected. It relaxes the layout chrome MainLayout owns so
 * the in-flow canvas reaches the viewport edges:
 *  - the `.pg-layout` side gutters + max-width collapse (KEEPING its `padding-top`, which carries the
 *    impersonation-strip offset `--ows-imp-offset`); (0,2,0) specificity beats the styled base, no !important
 *  - the content card loses its 24px corner radius (its bg/border are already stripped by LayoutBackdropReset)
 *  - the scroller drops the reserved stable-scrollbar gutter (else the board is ~6px short on the right) and
 *    clips instead of scrolling
 *  - the page column drops ALL padding (gutters, gaps, AND the top), so the board extends UP under the
 *    (transparent) top bar and reaches the viewport floor; the composer's own height anchor handles the rest
 *  - the header scrim is KEPT (not hidden) and raised to z-index 4: React Flow's own `.react-flow__pane` is
 *    z-index 1 and paints later in the DOM, so at the default z:1 the scrim would sit UNDER the board and do
 *    nothing; z:4 puts it above the pane (1) yet below the floating Panels / busy overlay (5) and the bar (30),
 *    so it fades the dot-grid under the top bar and the breadcrumb / wallet stay readable over the board
 */
export const ImmersiveLayout = createGlobalStyle`
  ${NAV_DESKTOP} {
    .pg-layout[data-immersive] {
      padding-left: 0;
      padding-right: 0;
      max-width: none;
    }

    /* The template ships config CSS for the .pg-layout .side-menu column (the 84px rail column's min/max
       width) at specificity (0,2,0), which OUT-RANKS the styled-component collapse (0,1,0) -- so in immersive
       the column kept reserving its 84px and the board started 84px in (the dead strip on the left;
       --content-left was stuck at 84px, confirmed in devtools). This (0,3,0) rule wins the cascade: collapse
       the column to 0 and pull Root's flex gap back so the board reclaims the space (the fixed rail itself
       slides out on its own transform). SideMenuCol keeps its transition, so the body still expands smoothly. */
    .pg-layout[data-immersive] .side-menu {
      min-width: 0;
      max-width: 0;
      margin-right: calc(-1 * ${LAYOUT_CHROME.gap});
    }

    .pg-layout[data-immersive] .pg-body-content {
      border-radius: 0;
    }

    .pg-layout[data-immersive] .ai-bb-page-root {
      overflow: hidden;
      scrollbar-gutter: auto;
    }

    .pg-layout[data-immersive] .ai-bb-page-col {
      padding: env(safe-area-inset-top) 0 0;
    }

    .pg-layout[data-immersive] .ai-bb-header-scrim {
      z-index: 4;
    }
  }
`;

/* Template `.side-menu` column. In immersive (full-screen) mode it collapses to 0 width so the body reclaims
 * the space; that collapse lives in ImmersiveLayout above (it must out-specify the template's own
 * `.pg-layout .side-menu` width rule, which a styled-component class cannot), and the transition here animates
 * it. The rail itself (position:fixed) slides out via its own transform (see SideMenu). */
export const SideMenuCol = styled.div.attrs({ className: 'side-menu' })`
  min-width: ${LAYOUT_CHROME.sideMenuWidth};
  max-width: ${LAYOUT_CHROME.sideMenuWidth};
  position: relative;
  display: none;
  transition:
    min-width 0.28s ease,
    max-width 0.28s ease,
    margin-right 0.28s ease;

  ${NAV_DESKTOP} {
    display: block;
  }
`;

/* Template `.pg-body`. */
export const Body = styled.div.attrs({ className: 'pg-body' })`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${FIXED_HEADER} {
    /* (The 76px top pad is zeroed by PageShell's ContentTuck on mobile; the
     * Page owns header clearance.) Grow with content so the body scrolls. */
    padding: 76px 12px 0 12px;
    height: auto;
    overflow: visible;
  }
`;

/* Template `.pg-body-content` — the page's center content lives inside. */
export const Content = styled.main.attrs({ className: 'pg-body-content' })`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  border: 1px solid ${LAYOUT_CHROME.contentBorder};
  background: ${LAYOUT_CHROME.contentBg};
  overflow: hidden;

  ${FIXED_HEADER} {
    /* Mobile: the card chrome (bg/border/radius) is already neutralised by
     * PageShell's LayoutBackdropReset; here we just stop owning a fixed height /
     * inner clip so the page flows into the document (body) scroll. */
    height: auto;
    overflow: visible;
  }
`;

/* Right-hand Lumio column — the template ships the panel (.oh-lc) but no column;
 * width + breakpoint are judgment calls recorded in the conversion report. */
export const LumioCol = styled.div<{ $collapsed?: boolean }>`
  min-width: ${({ $collapsed }) => ($collapsed ? '0px' : LAYOUT_CHROME.lumioColWidth)};
  max-width: ${({ $collapsed }) => ($collapsed ? '0px' : LAYOUT_CHROME.lumioColWidth)};
  position: relative;
  box-sizing: border-box;
  display: none;
  /* Collapse / expand: the column's width eases to 0 (the content widens into it) while the panel slides out to
   * the right and fades, so hiding Lumio is one continuous, elegant motion. */
  overflow: hidden;
  /* Collapsed, the column is also taken out of the tab order and the accessibility tree (visibility), switched
   * only once the slide has finished so the motion is not cut short; expanding shows it at once. */
  visibility: ${({ $collapsed }) => ($collapsed ? 'hidden' : 'visible')};
  transition:
    min-width 420ms cubic-bezier(0.32, 0.72, 0, 1),
    max-width 420ms cubic-bezier(0.32, 0.72, 0, 1),
    visibility 0s linear ${({ $collapsed }) => ($collapsed ? ANIM.LUMIO_DOCK_MS : 0)}ms;

  & > * {
    width: ${LAYOUT_CHROME.lumioColWidth};
    transform: translateX(${({ $collapsed }) => ($collapsed ? '32px' : '0')});
    opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
    transition:
      transform 420ms cubic-bezier(0.32, 0.72, 0, 1),
      opacity ${({ $collapsed }) => ($collapsed ? '220ms' : '360ms 80ms')} ease;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    & > * {
      transition: none;
    }
  }

  ${LUMIO_DESKTOP} {
    display: block;
    /* The Root drops its bottom pad on desktop so the content card bleeds to the
     * viewport edge; the Lumio panel shouldn't. Pad the column (it's border-box
     * and the panel is height:100%) so the panel rests the same 24px above the
     * bottom that the side menu and the drawer inset do — a balanced gap, not a
     * dead one. */
    padding-bottom: ${LAYOUT_CHROME.pad};
  }
`;

/**
 * MOBILE body-level scroll.
 *
 * The app shell pins `html, body, #root` to `height: 100%` (`src/index.scss`) so
 * the document never grows, and the nova content scrolls inside its own
 * `.ai-bb-page-root` container (`PageShell`). That inner scroll is invisible to
 * the browser chrome — on iOS the URL bar only hides/shows when the *document*
 * scrolls. So on phones we unpin that whole chain for as long as this layout is
 * mounted (it unmounts when you leave the area, restoring the pin) and let the
 * page's natural height drive the document, making the body the scroller.
 *
 * Scoped to `FIXED_HEADER` (≤899px) so desktop keeps its fixed-height,
 * inner-container scroll untouched. `!important` overrides the un-scoped, un-flagged
 * `height: 100%` from the global stylesheet and the styled-component defaults —
 * the same override pattern `PageShell`'s `ContentTuck` already uses. The
 * transition host keeps its `overflow-x: clip`, so the page slide stays hidden
 * (no stray horizontal scrollbar); only its height is freed.
 *
 * `useWindowScrollRestoration` (transitions/) restores per-page window scroll on
 * this breakpoint, the companion to the inner-container `useScrollRestoration`.
 * NB: making `.ai-bb-page-root` `overflow: visible` below is ALSO what renders
 * that inner hook inert here — `scrollTop` is a no-op on a non-scroll container —
 * so the window hook cleanly owns mobile and the inner hook owns desktop.
 */
export const MobileBodyScroll = createGlobalStyle`
  ${FIXED_HEADER} {
    html,
    body,
    #root {
      height: auto !important;
      overflow: visible !important;
    }
    /* Paint the document (incl. the safe areas exposed by viewport-fit=cover and
     * the rubber-band overscroll) our page black, so the strip below the iOS
     * toolbar / around the home indicator reads black instead of the browser grey
     * — this is the colour iOS shows when it re-reveals its controls. important
     * so the config-driven shell's own body background can't win it back. */
    html,
    body {
      background: ${COLORS.pageBg} !important;
    }
    html {
      min-height: 100dvh;
    }
    .ai-bb-page-transition {
      height: auto !important;
    }
    .ai-bb-page-root {
      height: auto !important;
      min-height: 0 !important;
      overflow: visible !important;
    }
    /* Keep the whole chain between a sticky page header (Key Metrics / Brand
     * metrics) and the document scroller free of scroll containers. position:
     * sticky silently STOPS pinning if ANY ancestor up to the scroller has
     * overflow hidden/auto/scroll — and these chrome columns carry overflow:
     * hidden in their desktop base, flipped to visible on mobile only by their own
     * (non-!important) styled rule, which a higher-specificity runtime rule could
     * beat. Pin them visible here so the sticky headers always pin on mobile.
     * (.ai-bb-page-transition keeps its overflow-x: clip above — clip does NOT
     * create a scroll container, so it never breaks sticky.) */
    .pg-layout,
    .pg-body,
    .pg-body-content {
      overflow: visible !important;
    }
  }
`;
